/*
 * component-shared-accordions.js — Slice 18 Phase A.
 *
 * Vanilla disclosure-widget behavior for the shared accordions block +
 * accordions section. Loaded via <script src=… defer> from the consumer
 * (browser dedupes by URL). Single document-level event delegation handles
 * all instances on the page.
 *
 * Behavior:
 *   - click on a .shared-accordion__trigger: toggle the parent .shared-accordion__item's
 *     `is-open` class, update aria-expanded, toggle `hidden` on the panel.
 *   - if wrapper carries data-single-open="true", close other open siblings.
 *   - on page load: open the first item per wrapper that carries
 *     data-open-first="true" AND has no item already in is-open state.
 *
 * Animation:
 *   - max-block-size transition runs via CSS. JS sets --acc-panel-max to
 *     the panel's scrollHeight on open (so it transitions to a real value),
 *     then back to 0 on close.
 *   - `hidden` attribute is added after the close transition via a one-shot
 *     transitionend listener filtered to max-block-size on the panel
 *     itself. Reverted to advisor-flagged transitionend pattern (cleaner
 *     than setTimeout(parseInt(transitionDuration)) which returned 0 for
 *     "0.25s" values).
 *
 * Rules in force:
 *   Rule J1  — no inline event handlers (addEventListener only)
 *   Rule J7  — document-level listener; no per-item observers required
 *   Rule J8  — no window.* namespace; pure IIFE
 *   Rule J12 — IIFE-dedup guard for multi-instance safety
 */
(function () {
  if (window.__BLOCKDEDUP_SHARED_ACCORDIONS__) return;
  window.__BLOCKDEDUP_SHARED_ACCORDIONS__ = true;

  function findItem(el) {
    return el.closest('[data-acc-item]');
  }
  function findWrapper(el) {
    return el.closest('[data-shared-accordions]');
  }

  function onCloseTransitionEnd(e) {
    // 6.0.3: Chrome/Safari report the physical name (max-height) for the logical property.
    if (e.target !== e.currentTarget || (e.propertyName !== 'max-block-size' && e.propertyName !== 'max-height')) return;
    var panel = e.currentTarget;
    panel.removeEventListener('transitionend', onCloseTransitionEnd);
    var item = panel.closest('[data-acc-item]');
    if (item && !item.classList.contains('is-open')) {
      panel.setAttribute('hidden', '');
    }
  }

  function onOpenTransitionEnd(e) {
    // 6.0.3: Chrome/Safari report the physical name (max-height) for the logical property.
    if (e.target !== e.currentTarget || (e.propertyName !== 'max-block-size' && e.propertyName !== 'max-height')) return;
    var panel = e.currentTarget;
    panel.removeEventListener('transitionend', onOpenTransitionEnd);
    var item = panel.closest('[data-acc-item]');
    if (item && item.classList.contains('is-open')) panel.style.setProperty('--acc-panel-max', 'none');
  }

  function setOpen(item, open) {
    var trigger = item.querySelector('.shared-accordion__trigger');
    var panel   = item.querySelector('.shared-accordion__panel');
    if (!trigger || !panel) return;

    // Always remove any pending close listener — covers the rapid reopen case
    // where the user clicks before the close transition completes.
    panel.removeEventListener('transitionend', onCloseTransitionEnd);

    panel.removeEventListener('transitionend', onOpenTransitionEnd);

    if (open) {
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      panel.style.setProperty('--acc-panel-max', panel.scrollHeight + 'px');
      // 6.0.3: once open, release the cap so a later reflow (fonts, CSS, resize) never cuts the answer.
      panel.addEventListener('transitionend', onOpenTransitionEnd);
    } else {
      // 6.0.3: pin the real height BEFORE dropping is-open (the open panel may be uncapped),
      // so the close animates from a concrete value instead of jumping from none to 0.
      panel.style.setProperty('--acc-panel-max', panel.scrollHeight + 'px');
      void panel.offsetHeight; // commit the start value
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.setProperty('--acc-panel-max', '0px');
      panel.addEventListener('transitionend', onCloseTransitionEnd);
    }
  }

  function onClick(e) {
    var trigger = e.target.closest('.shared-accordion__trigger');
    if (!trigger) return;
    var wrapper = findWrapper(trigger);
    if (!wrapper) return;
    var item = findItem(trigger);
    if (!item) return;

    var isOpen = item.classList.contains('is-open');

    if (!isOpen && wrapper.getAttribute('data-single-open') === 'true') {
      var siblings = wrapper.querySelectorAll('[data-acc-item].is-open');
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] !== item) setOpen(siblings[i], false);
      }
    }

    setOpen(item, !isOpen);
  }

  function initOpenFirst() {
    var wrappers = document.querySelectorAll('[data-shared-accordions][data-open-first="true"]');
    for (var i = 0; i < wrappers.length; i++) {
      var wrapper = wrappers[i];
      if (wrapper.querySelector('[data-acc-item].is-open')) continue;
      var first = wrapper.querySelector('[data-acc-item]');
      if (first) setOpen(first, true);
    }
  }

  function init() {
    document.addEventListener('click', onClick);
    // 6.0.3 fix: items that ship is-open (acc_open_by_default) used to get their
    // scrollHeight frozen here, at DOMContentLoaded, BEFORE the async accordion CSS
    // and fonts applied: the answer stayed cut at ~24px. They now stay uncapped
    // (CSS fallback); setOpen(false) measures the real height at close time.
    var openItems = document.querySelectorAll('[data-shared-accordions] [data-acc-item].is-open');
    for (var j = 0; j < openItems.length; j++) {
      var panel = openItems[j].querySelector('.shared-accordion__panel');
      if (panel) panel.style.setProperty('--acc-panel-max', 'none');
    }
    initOpenFirst();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
